import React from 'react';
import Select, {
  ActionMeta,
  ControlProps,
  MultiValue,
  OptionProps,
  components,
} from 'react-select';
import { AiManagementTeamMember } from 'store/modules/ai_management_store/types';
import AiManagementAvatar from 'components/atoms/ai_management_avatar';
import { Container } from './styles';

const ALL_MEMBERS = 0;

interface MemberOption {
  value: number;
  label: string;
  initials?: string;
}

interface AiManagementMembersFilterProps {
  team: AiManagementTeamMember[];
  memberIds: number[];
  scope: 'mine' | null;
  onChange: (memberIds: number[]) => void;
}

const MemberOptionItem = (props: OptionProps<MemberOption, true>) => {
  const { data, isSelected } = props;
  const all = data.value === ALL_MEMBERS;

  return (
    <components.Option {...props}>
      <div className="d-flex align-items-center">
        <input
          type="checkbox"
          className="member-checkbox"
          checked={isSelected}
          readOnly
          tabIndex={-1}
        />
        {!all && (
          <AiManagementAvatar
            initials={data.initials ?? ''}
            memberId={data.value}
            size={24}
            className="mr-2"
          />
        )}
        <span className="text-truncate">{data.label}</span>
      </div>
    </components.Option>
  );
};

const MembersControl = (props: ControlProps<MemberOption, true>) => {
  const { children } = props;

  return (
    <components.Control {...props}>
      <i className="material-icons text-secondary ml-2">group</i>
      <span className="members-label ml-2">Membros:</span>
      {children}
    </components.Control>
  );
};

const summaryOf = (
  scope: 'mine' | null,
  selected: AiManagementTeamMember[],
  memberIds: number[],
) => {
  if (scope === 'mine') return 'Por mim';
  if (memberIds.length === 0) return 'Todos da equipe';
  if (memberIds.length === 1) return selected[0]?.name ?? '1 membro';
  return `${memberIds.length} membros`;
};

const AiManagementMembersFilter: React.FC<AiManagementMembersFilterProps> = ({
  team,
  memberIds,
  scope,
  onChange,
}) => {
  const selected = team.filter((member) => memberIds.includes(member.id));
  const summary = summaryOf(scope, selected, memberIds);
  const active = scope === 'mine' || memberIds.length > 0;

  const options: MemberOption[] = [
    { value: ALL_MEMBERS, label: 'Todos da equipe' },
    ...team.map((member) => ({
      value: member.id,
      label: member.name,
      initials: member.initials,
    })),
  ];

  const value = options.filter((option) =>
    option.value === ALL_MEMBERS ? !active : scope !== 'mine' && memberIds.includes(option.value),
  );

  const handleChange = (_: MultiValue<MemberOption>, meta: ActionMeta<MemberOption>) => {
    const clicked = meta.option?.value;
    if (clicked === undefined) return;
    if (clicked === ALL_MEMBERS) {
      onChange([]);
      return;
    }

    const current = scope === 'mine' ? [] : memberIds;
    onChange(
      current.includes(clicked) ? current.filter((id) => id !== clicked) : [...current, clicked],
    );
  };

  return (
    <Container
      className="flex-shrink-0"
      active={active}
      title={memberIds.length > 1 ? selected.map((member) => member.name).join(', ') : undefined}
    >
      <Select<MemberOption, true>
        isMulti
        isSearchable={false}
        options={options}
        value={value}
        onChange={handleChange}
        placeholder={summary}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        backspaceRemovesValue={false}
        isClearable={false}
        components={{ Option: MemberOptionItem, Control: MembersControl }}
        noOptionsMessage={() => 'Nenhum membro no quadro'}
        classNamePrefix="members-select"
        aria-label="Membros"
      />
    </Container>
  );
};

export default AiManagementMembersFilter;
